// sentry.config.ts
import * as Sentry from '@sentry/angular';
import { Replay } from '@sentry/replay';
import { environment } from './environments/environment';

export function initSentry() {
  if (!environment.sentry?.enabled || !environment.sentry?.dsn) return;

  Sentry.init({
    dsn: environment?.sentry.dsn,

    environment: environment.production ? 'production' : 'development',
    release: environment.appVersion || '1.0.0',

    integrations: [
      Sentry.browserTracingIntegration(),

      new Replay({
        sessionSampleRate: 0.1,
        errorSampleRate: 1.0,
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
      }),
    ],

    tracesSampleRate: environment.production ? 0.2 : 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,

    beforeSend(event, hint) {
      const error = hint.originalException;

      if (error && typeof error === 'object' && 'message' in error) {
        const message = error.message as string;

        if (
          message.includes('Non-Error promise rejection captured') ||
          message.includes('ResizeObserver loop limit exceeded') ||
          message.includes('Script error')
        ) {
          return null;
        }
      }

      event.tags = {
        ...event.tags,
        component: 'angular-app',
        browser: navigator.userAgent.split(' ')[0],
      };

      return event;
    },

    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.category === 'console' && breadcrumb.level === 'log') {
        return null;
      }

      if (breadcrumb.category === 'navigation') {
        breadcrumb.data = {
          ...breadcrumb.data,
          timestamp: new Date().toISOString(),
        };
      }

      return breadcrumb;
    },

    debug: !environment.production,

    maxBreadcrumbs: 50,

    attachStacktrace: true,

    sendDefaultPii: false,

    normalizeDepth: 5,
  });

  Sentry.setContext('app_info', {
    name: 'Risk Release',
    version: environment.appVersion || '1.0.0',
    build_time: new Date().toISOString(),
  });
}

export class SentryService {
  static captureException(error: Error, context?: any) {
    Sentry.withScope((scope) => {
      if (context) {
        scope.setContext('additional_info', context);
      }
      Sentry.captureException(error);
    });
  }

  static captureMessage(message: string, level: Sentry.SeverityLevel = 'info', context?: any) {
    Sentry.withScope((scope) => {
      if (context) {
        scope.setContext('additional_info', context);
      }
      scope.setLevel(level);
      Sentry.captureMessage(message);
    });
  }

  static setUserContext(user: { id: string; email?: string; username?: string }) {
    Sentry.setUser(user);
  }

  static addBreadcrumb(message: string, category: string, level: Sentry.SeverityLevel = 'info') {
    Sentry.addBreadcrumb({
      message,
      category,
      level,
      timestamp: Date.now() / 1000,
    });
  }

  static setTag(key: string, value: string) {
    Sentry.setTag(key, value);
  }

  static setContext(key: string, context: any) {
    Sentry.setContext(key, context);
  }

  static startTransaction(name: string, op: string) {
    const startTime = Date.now();

    return {
      setStatus: (status: string) => {
        this.addBreadcrumb(`Transaction ${name} status: ${status}`, 'transaction');
      },
      finish: () => {
        const duration = Date.now() - startTime;
        this.addBreadcrumb(`Transaction ${name} completed in ${duration}ms`, 'transaction');
      },
      setTag: (key: string, value: string) => {
        Sentry.setTag(key, value);
      },
    };
  }
}
